import clsx from "clsx";
import React, { useEffect } from "react";
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  Box,
  ListItem,
  Stack,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useDebounce } from "../../hooks/useDebounce";
import useInfiniteScrollQuery from "../../hooks/useInfiniteScroll";
import { keepPreviousData } from "@tanstack/react-query";
import {
  ExerciseAssetSort,
  ExerciseAssetStatus,
  ExerciseAssetType,
} from "@growth-machine-llc/stridist-api-client";
import ExerciseAssetsService from "../../services/ExerciseAssetsService";
import { ManageLibraryExerciseType } from "./ManageLibraryExercise";
import EditIcon from "@mui/icons-material/Edit";
import MinimizedTooltip, { TooltipLine } from "../tooltip/MinimizedTooltip";
import { IMinimizedExerciseTooltipProps } from "./ExerciseLibraryExercise";
import { useCurrentUserId } from "../../hooks/useCurrentUser";

const useStyles = makeStyles((theme) => ({
  root: {},

  paper: {
    margin: 0,
  },

  popper: {
    zIndex: "99999 !important" as any,
    borderRadius: 12,
    boxShadow: theme.shadows[5],
  },
  groupHeader: {
    color: theme.palette.text.primary,
    fontWeight: "bold",
    fontSize: "1rem",
    lineHeight: "1.5rem",
  },
  listbox: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between !important",
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    cursor: "pointer",
    "& .edit-box": {
      display: "none",
    },
    "&:hover .edit-box": {
      display: "inline-flex",
    },
    padding: theme.spacing(1, 2),
  },
}));

export interface ExerciseAutocompleteProps<T>
  extends Omit<
    AutocompleteProps<T, true, false, any, any>,
    "options" | "onChange" | "onInputChange" | "renderInput"
  > {
  pageSize?: number;
  onCloseAutocomplete?: (options: T[]) => void;
  onInputChange?: (e: any, value: string, options: T[]) => void;
  renderCustomInput?: (
    params: AutocompleteRenderInputParams,
    isLoading: boolean,
  ) => React.ReactNode;
  onFetchingExercise?: (value: boolean) => void;
  onChange?: (value: T) => void;
  onDisableSave?: (value: boolean) => void;
  isOptionSelected?: boolean;
  exercisesType: ExerciseAssetType;
}

// TODO_exercise_library: Temporarily copied from admin exercise autocomplete, should be added common solution for all exercise autocompletes
const EXERCISE_AUTOCOMPLETE_PAGE_SIZE = 20;
export const EXERCISE_LIBRARY_KEY_AUTOCOMPLETE_QUERY_KEY =
  "exercises-library-autocomplete";

export const useMinimizedExerciseTooltipStyles = makeStyles((theme) => ({
  info: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    "&::before": {
      content: "''",
      display: "inline-block",
      width: 8,
      height: 8,
      borderRadius: "50%",
    },
  },

  custom: {
    "&::before": {
      backgroundColor: theme.palette.primary.main,
    },
  },

  public: {
    "&::before": {
      visibility: "hidden",
    },
  },
}));

export const MinimizedExerciseTooltip = (
  props: IMinimizedExerciseTooltipProps,
) => {
  const { canEdit } = props;
  const s = useMinimizedExerciseTooltipStyles();

  return (
    <MinimizedTooltip
      title={
        canEdit && (
          <Stack direction="column">
            <TooltipLine>
              Uploaded <br /> by <b>me</b>
            </TooltipLine>
          </Stack>
        )
      }
      placement={"left"}
      opacity={0.7}
      isExerciseLibrary={true}
    >
      <Typography
        className={clsx(s.info, canEdit ? s.custom : s.public)}
      ></Typography>
    </MinimizedTooltip>
  );
};

export function ExerciseLibraryAutocomplete<
  T extends ManageLibraryExerciseType,
>(props: ExerciseAutocompleteProps<T>) {
  const {
    className,
    pageSize = 50,
    inputValue,
    onInputChange,
    onFetchingExercise,
    onDisableSave,
    onCloseAutocomplete,
    onChange,
    classes,
    exercisesType,
    isOptionSelected,
    ...other
  } = props;

  const theme = useTheme();
  const [open, setOpen] = React.useState(false);

  const currentUserId = useCurrentUserId();

  const delayedQuery = useDebounce(inputValue, 250);
  const {
    data: exercisesData,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteScrollQuery({
    queryKey: [
      EXERCISE_LIBRARY_KEY_AUTOCOMPLETE_QUERY_KEY,
      { query: delayedQuery },
    ],
    queryFn: ({ pageParam = 1 }) =>
      ExerciseAssetsService.getExerciseAssets(
        pageParam as number,
        EXERCISE_AUTOCOMPLETE_PAGE_SIZE,
        inputValue === "" ? undefined : inputValue.trim(),
        ExerciseAssetStatus.ACTIVE,
        null,
        null,
        ExerciseAssetSort.NAME_ASC,
        exercisesType,
      ),
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, pages) =>
      lastPage?.hasNextPage ? pages.length + 1 : undefined,
  });

  const exercises = exercisesData?.pages.flatMap((page) => page.items) ?? [];

  const s = useStyles();

  const handleOpenAutocomplete = React.useCallback(() => {
    if (inputValue.length > 0 && !isOptionSelected) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [inputValue, isOptionSelected]);

  useEffect(() => {
    handleOpenAutocomplete();
  }, [inputValue, isOptionSelected]);

  useEffect(() => {
    const matchedExercise = exercises.find(
      (option) => option.name.trim() === inputValue.trim(),
    );

    if (onDisableSave) {
      onDisableSave(
        (!isOptionSelected && isFetching) ||
          (exercises.length > 0 && !isOptionSelected && !!matchedExercise) ||
          (open && inputValue.length > 0 && exercises.length > 0),
      );
    }
  }, [isFetching, onDisableSave, inputValue, exercises, isOptionSelected]);

  const handleChange = React.useCallback(
    (_, value: T) => {
      if (onChange) {
        onChange(value);
      }
    },
    [onChange],
  );

  return (
    <Autocomplete
      open={open}
      classes={{
        root: clsx(s.root, className),
        paper: s.paper,
        popper: s.popper,
        groupLabel: s.groupHeader,
        ...classes,
      }}
      forcePopupIcon={false}
      filterOptions={(options) => options}
      inputValue={inputValue}
      clearOnBlur={false}
      options={exercises as T[]}
      getOptionLabel={(option) => {
        return typeof option === "string" ? option : (option.name as string);
      }}
      freeSolo
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id} className={s.listbox}>
          <Box display="flex" alignItems="center" flexDirection={"row"} gap={1}>
            <MinimizedExerciseTooltip
              canEdit={option.coachId === currentUserId}
            />
            {option.name}
          </Box>
          <Box
            className="edit-box"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              paddingLeft: 1,
            }}
          >
            <EditIcon
              fontSize="inherit"
              sx={{
                fontSize: 18,
                color: theme.palette.primary.main,
              }}
            />
            <Typography
              sx={{
                padding: 0,
                fontSize: 14,
                color: theme.palette.primary.main,
              }}
            >
              Edit
            </Typography>
          </Box>
        </ListItem>
      )}
      onClose={(_, reason) => {
        if (onCloseAutocomplete && (reason === "blur" || reason === "escape")) {
          onCloseAutocomplete(exercises as T[]);
          setOpen(false);
        }
      }}
      groupBy={() => "or update existed one"}
      onChange={handleChange as any}
      onInputChange={(_, value) => onInputChange(_, value, exercises as T[])}
      renderInput={(params) =>
        props.renderCustomInput(
          {
            ...params,
            inputProps: {
              ...params.inputProps,
              onFocus: handleOpenAutocomplete,
            },
          },
          isFetching,
        )
      }
      {...other}
      openOnFocus={false}
      slotProps={{
        listbox: {
          onScroll: (event: React.SyntheticEvent) => {
            const listboxNode = event.currentTarget;
            if (
              Math.round(listboxNode.scrollTop) + listboxNode.clientHeight >
              listboxNode.scrollHeight - 100
            ) {
              if (!isFetching && hasNextPage) fetchNextPage();
            }
          },
        },
      }}
    />
  );
}
